import { CallEffect, ForkEffect, GetContextEffect, PutEffect, SelectEffect, TakeEffect } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  actionChannel,
  getContext,
  call,
  delay,
  put,
  race,
  select,
  takeLatest,
  take,
  throttle,
  cancel,
  takeEvery,
  fork,
  retry,
  SagaGenerator,
} from 'typed-redux-saga';
import { buffers, Channel, EventChannel } from 'redux-saga';
import { isNil } from 'lodash-es';
import {
  RobotCleaningTaskCleanedAreaByDayListResponse,
  RobotCleaningTaskCleanedHrsByDayListResponse,
  RobotCleaningTaskConsumablesByDayListResponse,
  RobotCleaningTaskDistanceDrivenByDayListResponse,
  RobotDashboardTasksCompletionStatisticResponse,
  RoutesNameListResponse,
} from '../../../interfaces/Robot.types';
import { SubscriptionMachineUpdateResult } from '../../../interfaces/Machine.types';
import {
  MachineUpdateConnectionStatus,
  MachineUpdateRobotStatus,
  MachineUpdateTelemetry,
  SubscriptionMachineEvent,
} from '../../../interfaces/MachineSubscription.types';
import {
  MachineDetailsPanelActions,
  MachineDetailsRobotRealTimePropertyChangedAction,
} from '../machineDetailsPanelActions';
import { selectData } from '../machineDetailsPanelSelectors';
import { MachineDetailsPanelRobotActions, SubscribeToMachineUpdateAction } from './machineDetailsPanelRobotSlice';
import { RobotCleaningControlActions } from './robotCleaningControl/robotCleaningControlSlice';
import { ROBOT_STATUS_REFRESH_DELAY_MS, ROBOT_STATUS_REFRESH_MAX_WAIT_MS } from './utils/constants';
import {
  MachineDetailsRobotListCleaningDataCleanedAreaRequestAction,
  MachineDetailsRobotListCleaningDataCleanedAreaSuccessAction,
  MachineDetailsRobotListCleaningDataCleanedHrsRequestAction,
  MachineDetailsRobotListCleaningDataCleanedHrsSuccessAction,
  MachineDetailsRobotListConsumablesRequestAction,
  MachineDetailsRobotListConsumablesSuccessAction,
  MachineDetailsRobotListCleaningDataDistanceDrivenRequestAction,
  MachineDetailsRobotListCleaningDataDistanceDrivenSuccessAction,
  MachineDetailsRobotListRoutesNameRequestAction,
  MachineDetailsRobotListRoutesNameSuccessAction,
  MachineDetailsRobotCleaningConsumptionSummaryErrorAction,
  MachineDetailsRobotCleaningConsumptionSummarySuccessAction,
  MachineDetailsRobotCleaningConsumptionSummaryRequestAction,
  MachineDetailsRobotCleaningKPIsErrorAction,
  MachineDetailsRobotCleaningKPIsRequestAction,
  MachineDetailsRobotCleaningKPIsSuccessAction,
  MachineDetailsRobotTaskCompletedHistoryErrorAction,
  MachineDetailsRobotTaskCompletedHistoryRequestAction,
  MachineDetailsRobotTaskCompletedHistorySuccessAction,
  MachineDetailsRobotCleaningTaskReportErrorAction,
  MachineDetailsRobotCleaningTaskReportRequestAction,
  MachineDetailsRobotCleaningTaskReportSuccessAction,
  MachineDetailsRobotPollGetExportCtrPdfRequestAction,
  MachineDetailsRobotPollGetExportCtrPdfSuccessAction,
  MachineDetailsRobotPollGetExportCtrPdfErrorAction,
  MachineDetailsRobotExportCtrPdfRequestAction,
  MachineDetailsRobotExportCtrPdfSuccessAction,
  MachineDetailsRobotExportCtrPdfErrorAction,
} from './machineDetailsPanelRobotActions.types';
import { IActionCreatorErrorOptions } from 'app/cross-cutting-concerns/state-management/interfaces/StateManagement.types';
import { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';
import { Optional } from 'lib/types/Optional';
import { SagaUtils } from 'app/cross-cutting-concerns/state-management/utils/SagaUtils';
import { GraphQlSubscriptionMessage } from 'app/cross-cutting-concerns/communication/interfaces/graphql.types';
import { RobotUtils } from 'app/utils/robot/RobotUtils';
import {
  AsyncJobStatus,
  MachineConnectionStatus,
  MachineUpdate,
  RobotStatus,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { CleaningTaskReportListResponse } from 'app/modules/cleaning/interfaces/CleaningTaskReport.types';
import {
  CleaningReportsExportGetFile,
  CleaningReportsExportRobotDetailsRequest,
} from 'app/modules/cleaning/interfaces/CleaningReport.types';
import { CleaningConstants } from 'app/modules/cleaning/CleaningConstants';

export function* getRoutesNameListSaga(
  action: MachineDetailsRobotListRoutesNameRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<RoutesNameListResponse>>
  | PutEffect<MachineDetailsRobotListRoutesNameSuccessAction>
  | PutEffect<PayloadAction<IActionCreatorErrorOptions>>,
  void,
  IDependencies
> {
  const { robotService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(robotService.listRoutesName, action.payload);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotGetRoutesNameListSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotGetRoutesNameListError({ error }));
  }
}

export function* getCleaningTaskCleanedAreaByDayListSaga(
  action: MachineDetailsRobotListCleaningDataCleanedAreaRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<RobotCleaningTaskCleanedAreaByDayListResponse>>
  | PutEffect<MachineDetailsRobotListCleaningDataCleanedAreaSuccessAction>
  | PutEffect<PayloadAction<IActionCreatorErrorOptions>>,
  void,
  IDependencies
> {
  const { robotService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(robotService.getCleaningTaskCleanedAreaByDayList, action.payload);

    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataCleanedAreaSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataCleanedAreaError({ error }));
  }
}

export function* getCleaningTaskCleanedHrsByDayListSaga(
  action: MachineDetailsRobotListCleaningDataCleanedHrsRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<RobotCleaningTaskCleanedHrsByDayListResponse>>
  | PutEffect<MachineDetailsRobotListCleaningDataCleanedHrsSuccessAction>
  | PutEffect<PayloadAction<IActionCreatorErrorOptions>>,
  void,
  IDependencies
> {
  const { robotService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(robotService.getCleaningTaskCleanedHrsByDayList, action.payload);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataCleanedHrsSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataCleanedHrsError({ error }));
  }
}

export function* getCleaningTaskConsumablesByDayListSaga(
  action: MachineDetailsRobotListConsumablesRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<RobotCleaningTaskConsumablesByDayListResponse>>
  | PutEffect<MachineDetailsRobotListConsumablesSuccessAction>
  | PutEffect<PayloadAction<IActionCreatorErrorOptions>>,
  void,
  IDependencies
> {
  const { robotService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(robotService.getCleaningTaskConsumablesByDayList, action.payload);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataConsumablesSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataConsumablesError({ error }));
  }
}

export function* getCleaningTaskDistanceDrivenByDayListSaga(
  action: MachineDetailsRobotListCleaningDataDistanceDrivenRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<Optional<RobotCleaningTaskDistanceDrivenByDayListResponse>>
  | PutEffect<MachineDetailsRobotListCleaningDataDistanceDrivenSuccessAction>
  | PutEffect<PayloadAction<IActionCreatorErrorOptions>>,
  void,
  IDependencies
> {
  const { robotService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(robotService.getCleaningTaskDistanceDrivenByDayList, action.payload);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataDistanceDrivenSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataConsumablesError({ error }));
  }
}

export function* onMachineTelemetryChanged(machineUpdated: MachineUpdate): Generator<PutEffect> {
  const machineUpdatedData = RobotUtils.getMachineUpdatedData(machineUpdated?.data) as Record<string, string>;

  const machineUpdatedStatusData: MachineUpdateTelemetry = {
    ...machineUpdated,
    data: machineUpdatedData,
  };

  yield* put(
    MachineDetailsPanelActions.robotByStatusRealTimePropertyChanged({ updatedData: machineUpdatedStatusData })
  );
}

export function* onMachineStatusChanged(machineUpdated: MachineUpdate): Generator<PutEffect> {
  const machineUpdatedData = RobotUtils.getMachineUpdatedData(machineUpdated?.data) as {
    robotStatus: RobotStatus;
  };

  const machineUpdatedStatusData: MachineUpdateRobotStatus = {
    ...machineUpdated,
    data: machineUpdatedData,
  };

  yield* put(
    MachineDetailsPanelActions.robotByStatusRealTimePropertyChanged({ updatedData: machineUpdatedStatusData })
  );
}

export function* onMachineConnectionStatusChanged(machineUpdated: MachineUpdate): Generator<PutEffect> {
  const machineUpdatedData = RobotUtils.getMachineUpdatedData(machineUpdated?.data) as {
    connectionStatus: MachineConnectionStatus;
  };

  const machineUpdatedStatusData: MachineUpdateConnectionStatus = {
    ...machineUpdated,
    data: machineUpdatedData,
  };
  yield* put(
    MachineDetailsPanelActions.robotByStatusRealTimePropertyChanged({ updatedData: machineUpdatedStatusData })
  );
}

// A subscription can miss a status event, which leaves the status stale until the next one. Read once things settle
// (see watchRefreshRobotStatusSaga), then applied like a status event.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* refreshRobotStatusSaga(): Generator<unknown, void, any> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  const machine = yield* select(selectData);

  if (!machine?.id) {
    return;
  }

  try {
    const response = yield* call(machineService.getRobotStatus, { id: machine.id });
    const latest = response?.machine?.data;
    const current = yield* select(selectData);

    // The drawer may have moved on to another machine meanwhile.
    if (!latest || current?.id !== machine.id) {
      return;
    }

    const base = { customerId: current.customerId ?? '', machineId: machine.id };

    // A status an event changed during the read is newer than the read, so it is kept.
    if (
      latest.robotStatus &&
      latest.robotStatus !== current.robotStatus &&
      current.robotStatus === machine.robotStatus
    ) {
      const update: MachineUpdateRobotStatus = {
        ...base,
        event: SubscriptionMachineEvent.MACHINE_STATUS_CHANGED,
        data: { robotStatus: latest.robotStatus },
      };
      yield* put(MachineDetailsPanelActions.robotByStatusRealTimePropertyChanged({ updatedData: update }));
    }

    if (
      latest.connectionStatus &&
      latest.connectionStatus !== current.connectionStatus &&
      current.connectionStatus === machine.connectionStatus
    ) {
      const update: MachineUpdateConnectionStatus = {
        ...base,
        event: SubscriptionMachineEvent.MACHINE_CONNECTION_STATUS_CHANGED,
        data: { connectionStatus: latest.connectionStatus },
      };
      yield* put(MachineDetailsPanelActions.robotByStatusRealTimePropertyChanged({ updatedData: update }));
    }
  } catch (error) {
    console.error(error);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* waitUntilQuiet(channel: Channel<{ type: string }>): Generator<unknown, void, any> {
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { next } = yield* race({ quiet: delay(ROBOT_STATUS_REFRESH_DELAY_MS), next: take(channel) });

    if (!next) {
      return;
    }
  }
}

// Debounced, but with a max wait so a steady stream of events while the robot cleans can't hold the refresh off.
// A trigger during a refresh stays buffered and starts the next round.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* watchRefreshRobotStatusSaga(): Generator<unknown, void, any> {
  const channel = yield* actionChannel(
    [MachineDetailsPanelRobotActions.refreshRobotStatus, RobotCleaningControlActions.deviceStateReported],
    buffers.sliding(1)
  );

  // eslint-disable-next-line no-constant-condition
  while (true) {
    yield* take(channel);
    yield* race({ quiet: call(waitUntilQuiet, channel), maxWait: delay(ROBOT_STATUS_REFRESH_MAX_WAIT_MS) });
    yield* call(refreshRobotStatusSaga);
  }
}

export function* onMachineRoutineDataCHanged(machineUpdated: MachineUpdate): Generator<PutEffect> {
  yield* put(MachineDetailsPanelActions.robotByRoutineRealTimePropertyChanged({ updatedData: machineUpdated }));
}

export function* onEveryMachineUpdateSaga(
  message: GraphQlSubscriptionMessage<SubscriptionMachineUpdateResult>
): Generator<
  | GetContextEffect
  | PutEffect<MachineDetailsRobotRealTimePropertyChangedAction>
  | PutEffect<ReturnType<typeof MachineDetailsPanelRobotActions.refreshRobotStatus>>
  | SelectEffect
  | ForkEffect
> {
  const machineUpdated = message.value.data.onMachineUpdate;
  const event = machineUpdated?.event;

  try {
    if (!isNil(event)) {
      switch (event) {
        case SubscriptionMachineEvent.TELEMETRY_CHANGED:
          yield* fork(onMachineTelemetryChanged, machineUpdated);
          break;

        case SubscriptionMachineEvent.MACHINE_STATUS_CHANGED:
          yield* fork(onMachineStatusChanged, machineUpdated);
          break;

        case SubscriptionMachineEvent.MACHINE_CONNECTION_STATUS_CHANGED:
          yield* fork(onMachineConnectionStatusChanged, machineUpdated);
          break;

        case SubscriptionMachineEvent.MACHINE_ROUTINE_CHANGED:
          yield* fork(onMachineRoutineDataCHanged, machineUpdated);
          // A routine starting or ending changes the robot's status too, whose own event may have been missed.
          yield* put(MachineDetailsPanelRobotActions.refreshRobotStatus());
          break;

        case SubscriptionMachineEvent.CLEANING_DATA_CHANGED:
          yield* put(MachineDetailsPanelRobotActions.refreshRobotStatus());
          break;

        default:
          break;
      }
    }
  } catch (error) {
    console.error('machineId', message.value?.data?.onMachineUpdate?.machineId);
    console.error(error);
  }
}

export function* subscribeToMachineUpdate(
  subscribeAction: SubscribeToMachineUpdateAction
): Generator<
  | GetContextEffect
  | CallEffect<EventChannel<GraphQlSubscriptionMessage<SubscriptionMachineUpdateResult>>>
  | ForkEffect<never>
  | TakeEffect
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const { observable, throttleDuration } = machineService.subscribeToMachineUpdate({
      customerId: subscribeAction.payload.customerId,
      machineId: subscribeAction.payload.machineId,
    });

    const channel = yield* call(
      SagaUtils.createChannelFromObservable<GraphQlSubscriptionMessage<SubscriptionMachineUpdateResult>>,
      observable
    );

    yield* throttle(throttleDuration, channel, onEveryMachineUpdateSaga);

    while (true) {
      const unsubscribeAction = yield* take(MachineDetailsPanelRobotActions.unsubscribeFromMachineUpdate);
      if (
        unsubscribeAction.payload.customerId === subscribeAction.payload.customerId &&
        unsubscribeAction.payload.machineId === subscribeAction.payload.machineId
      ) {
        channel.close();
        cancel();
      }
    }
  } catch (err) {
    console.error('Error while subscribing/unsubscribing to subscription:', err);
  }
}

export function* getRobotCleaningConsumptionSummarySaga(
  action: MachineDetailsRobotCleaningConsumptionSummaryRequestAction
): Generator<
  | GetContextEffect
  | CallEffect
  | PutEffect<MachineDetailsRobotCleaningConsumptionSummarySuccessAction>
  | PutEffect<MachineDetailsRobotCleaningConsumptionSummaryErrorAction>,
  void,
  IDependencies
> {
  const { machineService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(machineService.getMachineRobotConsumptionSummary, action.payload);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotCleaningConsumptionSummarySuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(
      MachineDetailsPanelRobotActions.machineDetailsRobotCleaningConsumptionSummaryError({
        error,
      })
    );
  }
}

export function* getTasksCompletionHistorySaga(
  action: MachineDetailsRobotTaskCompletedHistoryRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<RobotDashboardTasksCompletionStatisticResponse>
  | PutEffect<MachineDetailsRobotTaskCompletedHistorySuccessAction>
  | PutEffect<MachineDetailsRobotTaskCompletedHistoryErrorAction>,
  void,
  IDependencies
> {
  const { robotService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(robotService.getRobotDashboardTasksCompletion, action.payload);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotTaskCompletionHistorySuccess(response));
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDetailsPanelRobotActions.machineDetailsRobotTaskCompletionHistoryError({
        error,
      })
    );
  }
}

export function* getRobotCleaningKPIsSaga(
  action: MachineDetailsRobotCleaningKPIsRequestAction
): Generator<
  | GetContextEffect
  | CallEffect
  | PutEffect<MachineDetailsRobotCleaningKPIsSuccessAction>
  | PutEffect<MachineDetailsRobotCleaningKPIsErrorAction>,
  void,
  IDependencies
> {
  const { robotService } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(robotService.robotsKPIs, action.payload);
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotCleaningKPIsSuccess(response));
  } catch (error) {
    console.error(error);
    yield* put(
      MachineDetailsPanelRobotActions.machineDetailsRobotCleaningKPIsError({
        error,
      })
    );
  }
}

export function* getRobotCTRListSaga(
  action: MachineDetailsRobotCleaningTaskReportRequestAction
): Generator<
  | GetContextEffect
  | CallEffect<CleaningTaskReportListResponse>
  | PutEffect<MachineDetailsRobotCleaningTaskReportSuccessAction>
  | PutEffect<MachineDetailsRobotCleaningTaskReportErrorAction>,
  void,
  IDependencies
> {
  const { cleaningTaskReportService } = yield* getContext<IDependencies>('dependencies');

  try {
    const { append, ...requestOptions } = action.payload;
    const response = yield* call(cleaningTaskReportService.list, requestOptions);

    const successActionPayload = { ...response, append };
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotCleaningTaskReportsSuccess(successActionPayload));
  } catch (error) {
    console.error(error);
    yield* put(
      MachineDetailsPanelRobotActions.machineDetailsRobotCleaningTaskReportsError({
        error,
      })
    );
  }
}

export function* pollGetMachineRobotDetailsCtrPdfSaga(
  action: MachineDetailsRobotPollGetExportCtrPdfRequestAction
): Generator<
  | GetContextEffect
  | SagaGenerator<CleaningReportsExportGetFile, CallEffect<CleaningReportsExportGetFile>>
  | PutEffect<MachineDetailsRobotPollGetExportCtrPdfSuccessAction>
  | PutEffect<MachineDetailsRobotPollGetExportCtrPdfErrorAction>,
  void,
  any
> {
  const { cleaningReportService } = yield* getContext<IDependencies>('dependencies');
  const getExportCleaningReportsFile = async (): Promise<CleaningReportsExportGetFile> => {
    const getFileResponse = await cleaningReportService.getExportCleaningReportsFile(action.payload);
    const { status, presignedUrl } = getFileResponse?.cleaningReportsExportGetFile.data || {};
    if (status !== AsyncJobStatus.Done) {
      throw new Error('Download link is not ready yet!');
    }

    if (!presignedUrl) {
      throw new Error('Download link is ready but no presignedUrl!');
    }

    return getFileResponse;
  };

  try {
    const response: CleaningReportsExportGetFile = yield retry(
      CleaningConstants.CLEANING_REPORT_EXPORT_POLL_MAX_RETRIES,
      CleaningConstants.CLEANING_REPORT_EXPORT_POLL_INTERVAL,
      getExportCleaningReportsFile
    );

    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotPollGetExportCtrPdfSuccessAction(response));
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDetailsPanelRobotActions.machineDetailsRobotPollGetExportCtrPdfErrorAction({
        error,
      })
    );
  }
}

export function* requestExportMachineRobotDetailsCtrPdfSaga(
  action: MachineDetailsRobotExportCtrPdfRequestAction
): Generator<
  | GetContextEffect
  | SelectEffect
  | CallEffect<Optional<CleaningReportsExportRobotDetailsRequest> | void>
  | PutEffect<MachineDetailsRobotExportCtrPdfSuccessAction>
  | PutEffect<MachineDetailsRobotExportCtrPdfErrorAction>
  | PutEffect<MachineDetailsRobotPollGetExportCtrPdfRequestAction>,
  void,
  IDependencies
> {
  const { cleaningReportService, toastService, t } = yield* getContext<IDependencies>('dependencies');

  try {
    const response = yield* call(cleaningReportService.requestRobotDetailsReportsExport, action.payload);
    yield* call(toastService.success, {
      message: t('cleaningReportList.toast.success.message'),
      description: t('cleaningReportList.toast.success.description'),
    });
    const requestId = response?.robotDetailsReportsExportRequest?.data?.requestId;
    if (!requestId) {
      throw new Error('No requestId!');
    }

    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotPollGetExportCtrPdfRequestAction({ requestId }));
    yield* put(MachineDetailsPanelRobotActions.machineDetailsRobotExportCtrPdfSuccess(response));
  } catch (error) {
    console.error(error);

    yield* put(
      MachineDetailsPanelRobotActions.machineDetailsRobotExportCtrPdfError({
        error,
      })
    );
  }
}

export function* machineDetailsPanelRobotSaga(): Generator<ForkEffect, void> {
  yield* takeLatest(MachineDetailsPanelRobotActions.machineDetailsRobotGetRoutesNameListRequest, getRoutesNameListSaga);
  yield* takeLatest(
    MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataCleanedAreaRequest,
    getCleaningTaskCleanedAreaByDayListSaga
  );
  yield* takeLatest(
    MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataCleanedHrsRequest,
    getCleaningTaskCleanedHrsByDayListSaga
  );
  yield* takeLatest(
    MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataConsumablesRequest,
    getCleaningTaskConsumablesByDayListSaga
  );
  yield* takeLatest(
    MachineDetailsPanelRobotActions.machineDetailsRobotListCleaningDataDistanceDrivenRequest,
    getCleaningTaskDistanceDrivenByDayListSaga
  );
  yield* takeLatest(
    MachineDetailsPanelRobotActions.machineDetailsRobotCleaningConsumptionSummaryRequest,
    getRobotCleaningConsumptionSummarySaga
  );
  yield* takeLatest(
    MachineDetailsPanelRobotActions.machineDetailsRobotTaskCompletionHistoryRequest,
    getTasksCompletionHistorySaga
  );
  yield* takeLatest(MachineDetailsPanelRobotActions.machineDetailsRobotCleaningKPIsRequest, getRobotCleaningKPIsSaga);
  yield* takeLatest(MachineDetailsPanelRobotActions.machineDetailsRobotCleaningTaskReportsRequest, getRobotCTRListSaga);
  yield* takeLatest(
    MachineDetailsPanelRobotActions.machineDetailsRobotPollGetExportCtrPdfRequestAction,
    pollGetMachineRobotDetailsCtrPdfSaga
  );
  yield* takeLatest(
    MachineDetailsPanelRobotActions.machineDetailsRobotExportCtrPdfRequest,
    requestExportMachineRobotDetailsCtrPdfSaga
  );
  yield* takeEvery(MachineDetailsPanelRobotActions.subscribeToMachineUpdate, subscribeToMachineUpdate);
  // The device's own state (PRA) arrives through a separate subscription, so it catches status changes AM missed.
  yield* fork(watchRefreshRobotStatusSaga);
}
